import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangleIcon, BellIcon, CheckCircle2Icon, CreditCardIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon, Loader2Icon, PlusIcon, ReceiptIcon, ShieldAlertIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FigureBand } from '@/components/FigureBand';
import { LoadingBlock } from '@/components/LoadingBlock';
import { PageIntro } from '@/components/PageIntro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errorMessage } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { AGREEMENT_KIND_LABEL, INTERVAL_LABEL, INVOICE_STATUS_LABEL, billingApi, fmtDay, fmtMoney, type Agreement, type BillingAccount, type BillingSummaryResponse, type Invoice, type PaymentMethod, type Subscription } from '@/lib/billing';
import { fmtNumber, fmtRelative } from '@/lib/format';

export const BILLING_KEY = ['org', 'billing'] as const;

/** Organization → Billing: what you pay, what is due, how you pay, your agreements, and the notices we sent. */
export function BillingPage() {
  const q = useQuery({ queryKey: BILLING_KEY, queryFn: () => billingApi.summary(), staleTime: 30_000 });
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const { refreshMe } = useAuth();

  // Coming back from the payment provider: refresh and say what happened.
  useEffect(() => {
    const method = params.get('method');
    if (!method) return;
    if (method === 'saved') toast.success('Payment method saved');
    if (method === 'cancelled') toast('No payment method was added');
    const next = new URLSearchParams(params);
    next.delete('method');
    setParams(next, { replace: true });
    void qc.invalidateQueries({ queryKey: BILLING_KEY });
    void refreshMe();
  }, [params, setParams, qc, refreshMe]);

  if (q.isPending) return <LoadingBlock rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const data = q.data;

  if (!data.configured || !data.account) {
    return (
      <div className="space-y-5 sm:space-y-6">
        <PageIntro title="Billing" description="Invoices, payments and agreements for your organization." />
        <EmptyState icon={ReceiptIcon} title="Nothing to bill yet" description="Your account is set up during onboarding. Once an agreement is in place, invoices, payment details and receipts appear here." />
      </div>
    );
  }

  const openInvoices = data.invoices.filter((i) => i.status === 'open');
  const plan = data.subscriptions.find((s) => s.status === 'active' || s.status === 'paused');
  const s = data.summary;
  const currency = data.account.currency;
  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro title="Billing" description="What you pay, what is due, how you pay, the agreements behind it, and copies of every notice we sent." />
      <StateBanner account={data.account} summary={data.summary} />
      <FigureBand
        ariaLabel="Billing at a glance"
        figures={[
          { label: 'Amount due', value: fmtMoney(s?.amountDueCents ?? 0, currency), hint: s?.openInvoices ? `${fmtNumber(s.openInvoices)} open ${s.openInvoices === 1 ? 'invoice' : 'invoices'}${s.nextDueOn ? ` · next due ${fmtDay(s.nextDueOn)}` : ''}` : 'Nothing open' },
          { label: 'Overdue', value: fmtMoney(s?.overdueCents ?? 0, currency), bad: (s?.overdueCents ?? 0) > 0, hint: s?.overdueCents ? `Oldest ${fmtNumber(s.oldestOverdueDays)} days past due` : 'Nothing past due' },
          { label: plan ? INTERVAL_LABEL[plan.billingInterval] : 'Plan', value: plan ? fmtMoney(plan.recurringAmountCents, plan.currency) : '—', hint: plan ? `${plan.name}${plan.nextInvoiceOn ? ` · next invoice ${fmtDay(plan.nextInvoiceOn)}` : ''}` : 'No recurring plan' },
          { label: 'Payment', value: data.paymentMethods.length ? `${data.paymentMethods.length} ${data.paymentMethods.length === 1 ? 'card' : 'cards'}` : 'None saved', hint: data.account.collectionMethod === 'charge_automatically' ? 'Charged automatically when due' : `Net ${data.account.paymentTermsDays} · pay from the invoice` },
        ]}
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <InvoicesSection invoices={data.invoices} openCount={openInvoices.length} />
          <PlanSection subscriptions={data.subscriptions} agreements={data.agreements} />
          <AgreementsSection agreements={data.agreements} />
        </div>
        <div className="space-y-6">
          <PaymentMethodsSection methods={data.paymentMethods} account={data.account} provider={data.provider} />
          <ContactSection account={data.account} />
          <NoticesSection data={data} />
        </div>
      </div>
    </div>
  );
}

function Section({ title, description, actions, children }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-card border">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description ? <p className="text-muted-foreground mt-1 text-sm">{description}</p> : null}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function StateBanner({ account, summary }: { account: BillingAccount; summary: BillingSummaryResponse['summary'] }) {
  if (account.serviceState === 'active') return null;
  const due = summary ? fmtMoney(summary.overdueCents || summary.amountDueCents, account.currency) : null;
  return (
    <div role="alert" className={account.serviceState === 'suspended' ? 'border-destructive/40 bg-destructive/5 flex items-start gap-3 border p-4 text-sm' : 'border-warning/60 bg-warning/10 flex items-start gap-3 border p-4 text-sm'}>
      {account.serviceState === 'suspended' ? <ShieldAlertIcon className="text-destructive mt-0.5 size-5 shrink-0" aria-hidden /> : <AlertTriangleIcon className="mt-0.5 size-5 shrink-0" aria-hidden />}
      <div>
        <p className="font-medium">{account.serviceState === 'suspended' ? 'Sending texts is paused for your organization' : 'Your account has an overdue balance'}</p>
        <p className="text-muted-foreground mt-0.5">
          {account.serviceState === 'suspended' ? `Pay the open invoices${due ? ` (${due})` : ''} to restore sending right away. Signing in, history and contacts keep working.` : `${due ? `${due} is past due.` : ''}${account.graceEndsOn ? ` Pay by ${fmtDay(account.graceEndsOn)} to avoid an interruption.` : ''}`}
        </p>
      </div>
    </div>
  );
}

function InvoiceStatusBadge({ invoice }: { invoice: Invoice }) {
  if (invoice.status === 'open' && (invoice.daysOverdue ?? 0) > 0) return <Badge variant="danger">Overdue · {invoice.daysOverdue}d</Badge>;
  const variant = invoice.status === 'paid' ? 'success' : invoice.status === 'open' ? 'warning' : invoice.status === 'void' ? 'muted' : 'outline';
  return <Badge variant={variant}>{INVOICE_STATUS_LABEL[invoice.status]}</Badge>;
}

/** Invoices beyond this many fold behind "Show all", so years of billing do not stretch the page. */
const INVOICES_SHOWN = 12;

function InvoicesSection({ invoices, openCount }: { invoices: Invoice[]; openCount: number }) {
  const [all, setAll] = useState(false);
  const shown = all ? invoices : invoices.slice(0, INVOICES_SHOWN);
  return (
    <Section title="Invoices" description={openCount === 0 ? 'Nothing is due right now.' : `${openCount} open ${openCount === 1 ? 'invoice' : 'invoices'}. Open one to see the details or pay it.`}>
      {invoices.length === 0 ? (
        <p className="text-muted-foreground px-5 py-6 text-sm">No invoices yet.</p>
      ) : (
        <>
        <ul className="divide-y md:hidden" aria-label="Invoices">
          {shown.map((inv) => (
            <li key={inv.id}>
              <Link to={`/org/billing/invoices/${inv.id}`} className="active:bg-accent/40 grid gap-1 px-5 py-3">
                <span className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">{inv.number}</span>
                  <InvoiceStatusBadge invoice={inv} />
                </span>
                <span className="text-muted-foreground flex items-center justify-between gap-3 text-xs">
                  <span>Issued {fmtDay(inv.issuedOn)} · due {fmtDay(inv.dueOn)}</span>
                  <span className="text-foreground text-sm tabular-nums">{fmtMoney(inv.totalCents, inv.currency)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <Table className="hidden md:table">
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell>
                  <Link to={`/org/billing/invoices/${inv.id}`} className="font-medium underline-offset-4 hover:underline">
                    {inv.number}
                  </Link>
                  {inv.memo ? <div className="text-muted-foreground max-w-[22rem] truncate text-xs">{inv.memo}</div> : null}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{fmtDay(inv.issuedOn)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{fmtDay(inv.dueOn)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  <div>{fmtMoney(inv.totalCents, inv.currency)}</div>
                  {inv.status === 'open' && inv.amountPaidCents > 0 ? <div className="text-muted-foreground text-xs">{fmtMoney(inv.amountDueCents, inv.currency)} due</div> : null}
                </TableCell>
                <TableCell>
                  <InvoiceStatusBadge invoice={inv} />
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild size="sm" variant={inv.status === 'open' ? 'default' : 'outline'}>
                    <Link to={`/org/billing/invoices/${inv.id}`}>{inv.status === 'open' ? 'View & pay' : 'View'}</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {invoices.length > INVOICES_SHOWN && (
          <div className="flex justify-center border-t py-1.5">
            <Button type="button" variant="ghost" size="xs" onClick={() => setAll((v) => !v)}>
              {all ? 'Show recent only' : `Show all ${fmtNumber(invoices.length)} invoices`}
            </Button>
          </div>
        )}
        </>
      )}
    </Section>
  );
}

function PlanSection({ subscriptions, agreements }: { subscriptions: Subscription[]; agreements: Agreement[] }) {
  const active = subscriptions.filter((s) => s.status === 'active' || s.status === 'paused');
  return (
    <Section title="Your plan" description="What your organization pays and how often. Pricing follows your agreement; talk to us to change it.">
      {active.length === 0 ? (
        <p className="text-muted-foreground px-5 py-6 text-sm">No recurring plan is active.</p>
      ) : (
        <ul className="divide-y">
          {active.map((s) => {
            const agreement = agreements.find((a) => a.id === s.agreementId);
            return (
              <li key={s.id} className="grid gap-4 px-5 py-4 sm:grid-cols-[1fr_auto]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    {s.status === 'paused' ? <Badge variant="warning">Paused</Badge> : null}
                    {s.cancelAtPeriodEnd ? <Badge variant="muted">Ends {fmtDay(s.currentPeriodEnd)}</Badge> : null}
                  </div>
                  <ul className="text-muted-foreground mt-2 space-y-1 text-sm">
                    {s.lineItems.map((l) => (
                      <li key={l.id} className="flex justify-between gap-4">
                        <span>
                          {l.description}
                          {l.kind === 'usage' ? <span className="text-xs"> · usage{l.includedQuantity ? `, ${l.includedQuantity.toLocaleString()} included` : ''}</span> : l.kind === 'one_time' ? <span className="text-xs"> · one-time{l.billed ? ', billed' : ''}</span> : null}
                        </span>
                        <span className="tabular-nums">
                          {l.kind === 'usage' ? `${fmtMoney(l.unitAmountCents, s.currency)} each` : fmtMoney(l.quantity * l.unitAmountCents, s.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {agreement ? `${AGREEMENT_KIND_LABEL[agreement.kind]} "${agreement.title}" · ` : ''}
                    {s.termMonths ? `${s.termMonths}-month term${s.autoRenew ? ', renews automatically' : ''}${s.endsOn ? ` · through ${fmtDay(s.endsOn)}` : ''}` : 'No fixed term'}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold tabular-nums">{fmtMoney(s.recurringAmountCents, s.currency)}</div>
                  <div className="text-muted-foreground text-xs">{INTERVAL_LABEL[s.billingInterval]}</div>
                  {s.nextInvoiceOn ? <div className="text-muted-foreground mt-1 text-xs">Next invoice {fmtDay(s.nextInvoiceOn)}</div> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function AgreementsSection({ agreements }: { agreements: Agreement[] }) {
  const download = async (a: Agreement) => {
    try {
      const { url } = await billingApi.agreementFile(a.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Section title="Agreements" description="Contracts and statements of work between your organization and us.">
      {agreements.length === 0 ? (
        <p className="text-muted-foreground px-5 py-6 text-sm">No agreements on file.</p>
      ) : (
        <ul className="divide-y">
          {agreements.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <FileTextIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{a.title}</span>
                  <Badge variant={a.status === 'active' || a.status === 'signed' ? 'success' : a.status === 'expired' || a.status === 'terminated' ? 'muted' : 'outline'}>{a.status}</Badge>
                </div>
                <div className="text-muted-foreground text-xs">
                  {AGREEMENT_KIND_LABEL[a.kind]}
                  {a.reference ? ` · ${a.reference}` : ''}
                  {a.startsOn ? ` · ${fmtDay(a.startsOn)}${a.endsOn ? ` – ${fmtDay(a.endsOn)}` : ''}` : ''}
                  {a.autoRenew ? ' · renews automatically' : ''}
                </div>
              </div>
              {a.file ? (
                <Button variant="outline" size="sm" onClick={() => void download(a)}>
                  <DownloadIcon aria-hidden /> Document
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PaymentMethodsSection({ methods, account, provider }: { methods: PaymentMethod[]; account: BillingAccount; provider: BillingSummaryResponse['provider'] }) {
  const qc = useQueryClient();
  const [removing, setRemoving] = useState<PaymentMethod | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: BILLING_KEY });

  const add = async () => {
    setBusy('add');
    try {
      const { url } = await billingApi.setupPaymentMethod();
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(null);
    }
  };
  const setDefault = useMutation({ mutationFn: (id: string) => billingApi.setDefaultPaymentMethod(id), onSuccess: () => void refresh(), onError: (err) => toast.error(errorMessage(err)) });
  const remove = useMutation({
    mutationFn: (id: string) => billingApi.removePaymentMethod(id),
    onSuccess: () => {
      setRemoving(null);
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const portal = async () => {
    setBusy('portal');
    try {
      const { url } = await billingApi.portal();
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(null);
    }
  };

  return (
    <Section title="Payment method" description={account.collectionMethod === 'charge_automatically' ? 'Invoices are charged to your default method when due.' : 'Saved cards make paying invoices one click.'}>
      <div className="space-y-3 px-5 py-4">
        {methods.length === 0 ? <p className="text-muted-foreground text-sm">No saved payment method.</p> : null}
        {methods.map((m) => (
          <div key={m.id} className="flex items-center gap-3 border p-3">
            <CreditCardIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium capitalize">
                {m.brand ?? m.kind} ···· {m.last4 ?? '????'} {m.isDefault ? <Badge variant="secondary">Default</Badge> : null}
              </div>
              {m.expMonth && m.expYear ? <div className="text-muted-foreground text-xs">Expires {String(m.expMonth).padStart(2, '0')}/{m.expYear}</div> : null}
            </div>
            {!m.isDefault ? (
              <Button variant="ghost" size="sm" onClick={() => setDefault.mutate(m.id)} disabled={setDefault.isPending}>
                Make default
              </Button>
            ) : null}
            <Button variant="ghost" size="icon-sm" aria-label="Remove payment method" onClick={() => setRemoving(m)}>
              <Trash2Icon aria-hidden />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void add()} disabled={!provider.available || busy !== null}>
            {busy === 'add' ? <Loader2Icon className="animate-spin" aria-hidden /> : <PlusIcon aria-hidden />} Add a card
          </Button>
          {provider.portal && methods.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => void portal()} disabled={busy !== null}>
              <ExternalLinkIcon aria-hidden /> Manage with {provider.name === 'stripe' ? 'Stripe' : provider.name}
            </Button>
          ) : null}
        </div>
        {!provider.available ? <p className="text-muted-foreground text-xs">Online payments are not available yet. Pay by bank transfer as described on the invoice.</p> : null}
      </div>
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Remove this payment method?" description="Automatic charges will fail until another method is saved." confirmLabel="Remove" destructive loading={remove.isPending} onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
      />
    </Section>
  );
}

function ContactSection({ account }: { account: BillingAccount }) {
  const qc = useQueryClient();
  const [name, setName] = useState(account.billingName ?? '');
  const [email, setEmail] = useState(account.billingEmail ?? '');
  const [cc, setCc] = useState(account.ccEmails.join(', '));
  const [line1, setLine1] = useState(account.address.line1 ?? '');
  const [city, setCity] = useState(account.address.city ?? '');
  const [state, setState] = useState(account.address.state ?? '');
  const [postal, setPostal] = useState(account.address.postalCode ?? '');
  const [taxId, setTaxId] = useState(account.taxId ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      billingApi.updateAccount({
        billingName: name.trim() || null,
        billingEmail: email.trim() || null,
        ccEmails: cc
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        address: { ...account.address, line1: line1.trim() || undefined, city: city.trim() || undefined, state: state.trim() || undefined, postalCode: postal.trim() || undefined },
        taxId: taxId.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Billing contact saved');
      setError(null);
      void qc.invalidateQueries({ queryKey: BILLING_KEY });
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Section title="Billing contact" description="Invoices, reminders and receipts go here.">
      <form
        className="grid gap-3 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormError message={error} />
        <Field label="Billed to" htmlFor="b-name">
          <Input id="b-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Accounts payable" />
        </Field>
        <Field label="Email" htmlFor="b-email">
          <Input id="b-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ap@company.com" />
        </Field>
        <Field label="Also send to" htmlFor="b-cc" description="Comma-separated.">
          <Input id="b-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="finance@company.com" />
        </Field>
        <Field label="Address" htmlFor="b-line1">
          <Input id="b-line1" value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="Street" />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Input aria-label="City" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
          <Input aria-label="State" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
          <Input aria-label="Postal code" value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="ZIP" />
        </div>
        <Field label="Tax ID" htmlFor="b-tax">
          <Input id="b-tax" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="Optional" />
        </Field>
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Section>
  );
}

function NoticesSection({ data }: { data: BillingSummaryResponse }) {
  const qc = useQueryClient();
  const me = useMe();
  const unread = data.notifications.filter((n) => !n.readAt).length;
  const markRead = useMutation({ mutationFn: () => billingApi.markNotificationsRead(), onSuccess: () => void qc.invalidateQueries({ queryKey: BILLING_KEY }) });
  useEffect(() => {
    if (unread > 0 && !markRead.isPending) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);
  return (
    <Section title="Notices" description={`Copies of what we emailed ${data.account?.billingEmail ?? me.user.email}.`}>
      {data.notifications.length === 0 ? (
        <p className="text-muted-foreground px-5 py-6 text-sm">No notices yet.</p>
      ) : (
        <ul className="divide-y">
          {data.notifications.map((n) => (
            <li key={n.id} className="flex items-start gap-3 px-5 py-3 text-sm">
              {n.kind === 'payment_receipt' ? <CheckCircle2Icon className="text-success mt-0.5 size-4 shrink-0" aria-hidden /> : n.kind.startsWith('service_') || n.kind === 'payment_failed' ? <AlertTriangleIcon className="text-warning mt-0.5 size-4 shrink-0" aria-hidden /> : <BellIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />}
              <div className="min-w-0">
                <div className="font-medium">{n.subject ?? n.kind.replace(/_/g, ' ')}</div>
                {n.bodyText ? <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{n.bodyText}</p> : null}
                <div className="text-muted-foreground mt-0.5 text-xs">
                  {fmtRelative(n.createdAt)}
                  {n.invoiceId ? (
                    <>
                      {' · '}
                      <Link to={`/org/billing/invoices/${n.invoiceId}`} className="underline-offset-4 hover:underline">
                        open invoice
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
